import {useMutation, useQuery, useQueryClient} from '@github-ui/react-query'
import {verifiedFetchJSON} from '@github-ui/verified-fetch'

export type CanCommitStatus = 'blocked' | 'can_bypass' | 'allowed'

export type TriageAgentConfigCommitInfo = {
  can_commit_status: CanCommitStatus
  author_emails: string[]
  default_email: string
  default_new_branch_name: string
  commit_oid: string
  dco_signoff_enabled: boolean
}

export type TriageAgentConfigPayload = {
  exists: boolean
  content: string
  default_prompt: string
  blob_oid: string | null
  config_path: string
  default_branch: string
  commit_info: TriageAgentConfigCommitInfo
}

export type SaveConfigParams = {
  content: string
  message: string
  description: string
  commit_choice: 'direct' | 'quick-pull'
  target_branch?: string
  author_email?: string
  blob_oid?: string | null
}

export type SaveConfigResult = {
  ok: boolean
  redirect_url?: string
  pull_request_url?: string
  error?: string
}

function getConfigQueryKey(apiUrl: string) {
  return ['triage-agent-config', apiUrl]
}

export function useTriageAgentConfig(repoOwner: string, repoName: string, enabled: boolean) {
  const apiUrl = `/${repoOwner}/${repoName}/triage-agent/config`

  return useQuery({
    queryKey: getConfigQueryKey(apiUrl),
    queryFn: async () => {
      const response = await verifiedFetchJSON(apiUrl)
      if (!response.ok) {
        throw new Error('Failed to fetch triage agent config')
      }
      return response.json() as Promise<TriageAgentConfigPayload>
    },
    enabled,
  })
}

export function useSaveTriageAgentConfig(repoOwner: string, repoName: string) {
  const queryClient = useQueryClient()
  const apiUrl = `/${repoOwner}/${repoName}/triage-agent/config`
  const statusUrl = `/${repoOwner}/${repoName}/triage-agent`

  return useMutation({
    mutationFn: async (params: SaveConfigParams) => {
      const response = await verifiedFetchJSON(apiUrl, {
        method: 'POST',
        body: params,
      })
      if (!response.ok) {
        let body: {error?: string} = {}
        try {
          body = await response.json()
        } catch {
          // ignore parse errors
        }
        throw new Error(body?.error || 'Failed to save triage agent config')
      }
      return response.json() as Promise<SaveConfigResult>
    },
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: getConfigQueryKey(apiUrl)})
      // Invalidate the triage agent status query so the overlay reflects new state
      queryClient.invalidateQueries({queryKey: ['triage-agent-status', statusUrl]})
    },
  })
}
